import { useContext, useEffect } from 'react';

import { Container, LoadingOlist } from 'components/layout';
import { AuthContext } from 'contexts/auth';
import useMixPanel from 'hooks/use-mixpanel';
import { Trans, useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useAsyncFn } from 'react-use';
import Api from 'service';
import { SUBSCRIPTION_PLAN_CHOICE_URL } from 'utils/urls';

import { Button, ParagraphMd, SectionSubheading } from '@olist/design-system';
import { ChevronRightOutline, SuccessCircleFill } from '@olist/design-system-icons';
import { Box, Flex } from '@olist/united';

import { PresentationImgM, PresentationImgW } from '../../images';
import {
  ImageContainer,
  PresentationImage,
  PresentationList,
  SubTitle,
} from './plan-presentation.styles';

const PlanPresentation = () => {
  const { t } = useTranslation();

  const history = useHistory();
  const { userInfo } = useContext(AuthContext);
  const [{ error, loading, value: experimentData }, getConversionExperiment] = useAsyncFn(
    async () => Api.getUnleashExperiment('conversion_payment', userInfo?.leadId),
    [userInfo]
  );
  const { track } = useMixPanel();

  useEffect(() => {
    if (userInfo?.leadId && !experimentData && !loading) {
      getConversionExperiment();
    }
  }, [userInfo, getConversionExperiment, experimentData, loading]);

  const handleNavigateToChoosePlan = () => {
    track('Plan Presentation Page - Clicked in Choose My Plan');
    history.push(SUBSCRIPTION_PLAN_CHOICE_URL);
  };

  useEffect(() => {
    if (experimentData === false) {
      track('Experiment Plan Presentation', {
        'Variant Name': 'Redirected to Plan Choice',
      });

      history.push(SUBSCRIPTION_PLAN_CHOICE_URL);
    } else if (experimentData === true) {
      track('Experiment Plan Presentation', {
        'Variant Name': 'Viewed Plan Presentation Page',
      });
    }
  }, [experimentData, history, track]);

  useEffect(() => {
    if (error) {
      track('Plan Presentation Page - Redirected to Plan Choice With Error');
      history.push(SUBSCRIPTION_PLAN_CHOICE_URL);
    }
  }, [error, history, track]);

  if (loading || !experimentData) {
    return <LoadingOlist data-testid="qualification-loading-olist" />;
  }

  return (
    <Container mb={8}>
      <Flex
        my={3}
        width={1}
        flexDirection={['column-reverse', 'column-reverse', 'column-reverse', 'row']}
        alignItems={['flex-start', 'flex-start', 'flex-start', 'center']}
        height="100%"
        justifyContent="space-between"
      >
        <Flex maxWidth="500px" flexDirection="column" p={[0, 0, 0, 2]}>
          <SectionSubheading type="large">
            {t`The tool that will leverage your sales within marketplaces`}
          </SectionSubheading>
          <Box mt={3}>
            <SubTitle type="default">
              {/* @ts-ignore */}
              <Trans i18nKey="solutionPerToPer">
                <b>olist store</b> has end-to-end solutions that take your business further!
              </Trans>
            </SubTitle>
          </Box>

          <PresentationList>
            <li>
              <SuccessCircleFill />
              <ParagraphMd type="default">
                <b>{t`more sales`}</b>
              </ParagraphMd>
            </li>
            <li>
              <SuccessCircleFill />
              <ParagraphMd type="default">
                <b>{t`more efficiency`}</b>
              </ParagraphMd>
            </li>
            <li>
              <SuccessCircleFill />
              <ParagraphMd type="default">
                <b>{t`more intelligence`}</b>
              </ParagraphMd>
            </li>
          </PresentationList>
          <Box>
            <Button
              type="button"
              variation="primary"
              iconRight={ChevronRightOutline}
              onClick={handleNavigateToChoosePlan}
            >
              {t`choose my plan`}
            </Button>
          </Box>
        </Flex>
        <ImageContainer>
          <PresentationImage
            id="presentation-img-w"
            src={PresentationImgW}
            alt={t`a happy man and woman doing business with olist`}
          />
          <PresentationImage
            id="presentation-img-m"
            src={PresentationImgM}
            alt={t`a happy man and woman doing business with olist`}
          />
        </ImageContainer>
      </Flex>
    </Container>
  );
};

export { PlanPresentation };
